// =====================================================================
// Cloudare site configuration — single source of truth.
// Any phone / email / social / URL change happens here, NOT in components.
// =====================================================================

export const SITE = {
  name: "Cloudare Technologies",
  legalName: "Cloudare Technologies Private Limited",
  url: "https://www.cloudare.in",
  description:
    "Cloudare — India's leading E-Invoicing and ERP compliance provider. ZATCA, LHDN, GST, Oracle Fusion, EBS, NetSuite, Zoho, Odoo.",
};

export const CONTACT = {
  // Primary email for all inbound enquiries
  email: "hello@cloudare.in",
  // Secondary email used in CRM workflows
  salesEmail: "sales@cloudare.in",

  // Phone (display + e164 for href)
  phoneDisplay: "+1 628-282-5683",
  phoneHref: "+16282825683",
  whatsapp: "https://wa.me/+16282825683",

  // Calendly booking
  calendly: "https://calendly.com/cloudare",

  // Where contact form submissions go
  formEndpoint: "/api/contact",
};

export const SOCIAL = {
  linkedin: "https://www.linkedin.com/company/cloudare",
  // Unified identity — single X/Twitter handle
  twitter: "https://x.com/CloudareGlobal",
  instagram: "https://www.instagram.com/cloudaretechnologies/",
  facebook: "https://www.facebook.com/cloudaretech",
  // Served via Vercel reverse-proxy from blogs.cloudare.in (same-domain for SEO)
  blog: "/blog",
  blogExternal: "https://blogs.cloudare.in/",
  careers: "https://job.cloudare.in/jobs/Careers",
  // Cloudare network properties
  einvoiceGlobal: "https://einvoice.global",
  edynoor: "https://edynoor.com",
};

// E-invoicing / tax-tech ecosystem partners. Cloudare is vendor-neutral —
// we integrate with whichever GSP/ASP/PDP a client prefers. Listed
// alphabetically; presence here denotes integration capability, not
// exclusivity or endorsement.
export const ECOSYSTEM_PARTNERS = [
  { name: "Avalara", domain: "avalara.com", role: "Global tax compliance & e-invoicing" },
  { name: "BDO", domain: "bdo.global", role: "Advisory & compliance" },
  { name: "Clear (ClearTax)", domain: "clear.in", role: "GSP / IRP, India & ME e-invoicing" },
  { name: "Complyance", domain: "complyance.io", role: "Global e-invoicing platform" },
  { name: "Cygnet", domain: "cygnet.one", role: "GSP / IRP, India & global e-invoicing" },
  { name: "ecosio", domain: "ecosio.com", role: "EDI & PEPPOL access point" },
  { name: "Pagero", domain: "pagero.com", role: "Global network & PEPPOL access point" },
  { name: "Taxilla", domain: "taxilla.com", role: "GSP / e-invoicing & reconciliation" },
];

// Office addresses
export const OFFICES = [
  {
    title: "Odisha, India",
    lines: ["Arya Gali, Kantabanji", "Bolangir – 767039", "Odisha, India"],
  },
  {
    title: "Chhattisgarh, India",
    lines: [
      "7th Floor, Currency Tower",
      "VIP Road, Telibandha",
      "Raipur, Chhattisgarh – 492001",
    ],
  },
  {
    title: "Dubai, UAE",
    lines: ["6th Floor Al Meydan Rd", "Nad Al Sheba 1", "Dubai, UAE"],
  },
  {
    title: "Michigan, USA",
    lines: [
      "2222 W. Grand River Ave, Ste A",
      "Okemos, MI – 48864",
      "United States",
    ],
  },
  {
    title: "Ontario, Canada",
    lines: ["10 Lagerfeld Drive", "Brampton, Ontario", "Canada"],
  },
];

// Per-route metadata for per-page SEO and canonical correctness
// path key MUST match App.js Route path exactly
export const PAGE_META = {
  "/": {
    title: "Cloudare — Oracle ERP & Global E-Invoicing Compliance Partner",
    description:
      "Oracle Fusion, EBS, OIC and Zoho implementation across UAE PINT AE, KSA ZATCA, India GST and Malaysia LHDN. ASP-neutral. Enterprise-grade.",
  },
  "/about": {
    title: "About Cloudare — Oracle and Zoho Compliance Practice",
    description:
      "DPIIT-recognized, founded 2017. Entities in India, UAE, US. Oracle Fusion, EBS, OIC and global e-invoicing implementation for enterprises.",
  },
  "/contact-us": {
    title: "Contact Cloudare — Talk to an ERP or Compliance Specialist",
    description:
      "Reach Cloudare for Oracle ERP, e-invoicing compliance, middleware or staffing. India, UAE, US offices. Book a discovery call.",
  },
  "/locations": {
    title: "Our Locations — India, UAE, United States, United Kingdom",
    description:
      "Cloudare offices and delivery centres across India (Raipur, Kantabanji), UAE (Dubai), United States and the United Kingdom.",
  },
  "/hire-talent": {
    title: "Hire Top Tech & Non-Tech Talent | Cloudare Staffing",
    description:
      "Hire vetted developers, sales, product and marketing talent in days, not months. Cloudare staffing for startups and enterprises.",
  },
  "/staffing": {
    title: "Global IT Staffing — Oracle, SAP, E-Invoicing Specialists",
    description:
      "Oracle, SAP and e-invoicing talent across India, UAE, KSA, US (C2C/W2) and UK. RPO, contract, contract-to-hire and permanent models.",
  },
  "/apply": {
    title: "Apply to Cloudare — Open Roles in ERP and Compliance",
    description:
      "Open roles for Oracle Fusion, EBS, OIC, ZATCA, LHDN and GST specialists across India, UAE and remote. Apply via our Zoho Recruit portal.",
  },
  "/payroll": {
    title: "Payroll and HR Services — Multi-Geography Payroll Operations",
    description:
      "Payroll, statutory compliance and HR operations across India, UAE and US. Integration with Oracle HCM, Zoho People and SAP SuccessFactors.",
  },
  "/consultation": {
    title: "Business Consultation Services | Cloudare",
    description:
      "Strategic business and technology consultation across ERP, automation, analytics, cloud and enterprise applications.",
  },
  "/faq": {
    title: "FAQ — UAE PINT AE, KSA ZATCA, India GST, LHDN and Oracle",
    description:
      "Frequently asked questions on UAE e-invoicing, KSA ZATCA Phase 2, India GST, Malaysia LHDN MyInvois and Oracle Fusion implementation.",
  },
  "/solutions": {
    title: "Cloudare Solutions — ERP, Compliance, Middleware, Staffing",
    description:
      "End-to-end solutions across Oracle ERP, global e-invoicing compliance, middleware engineering and IT staffing for enterprise customers.",
  },
  "/tax/my": {
    title: "Malaysia LHDN E-Invoicing Solutions | Cloudare",
    description:
      "LHDN-aligned e-invoicing for Malaysia. End-to-end implementation, ERP integration and MyInvois compliance.",
  },
  "/tax/ksa": {
    title: "Saudi Arabia ZATCA E-Invoicing Solutions | Cloudare",
    description:
      "ZATCA Phase II compliant e-invoicing for Saudi Arabia. ERP integration, Fatoora portal connectivity and end-to-end support.",
  },
  "/einv": {
    title: "What is E-Invoicing? — A Practical Guide | Cloudare",
    description:
      "Understand electronic invoicing, why it matters and how to comply across India, KSA, UAE, Malaysia and beyond.",
  },
  "/peppol": {
    title: "PEPPOL Network — Cross-border E-Invoicing | Cloudare",
    description:
      "Learn how PEPPOL enables secure cross-border e-invoicing and how Cloudare helps you connect.",
  },
  "/ImpactOfEinv": {
    title: "Business Impact of E-Invoicing | Cloudare",
    description:
      "How e-invoicing affects compliance, working capital, vendor reconciliation and digital transformation.",
  },
  "/HowToComply": {
    title: "How to Comply with E-Invoicing Regulations | Cloudare",
    description:
      "A step-by-step compliance guide for ZATCA, LHDN, FTA UAE and GST e-invoicing mandates.",
  },
  "/CloudareForEinv": {
    title: "How Cloudare Helps with E-Invoicing | Cloudare",
    description:
      "Cloudare automates and simplifies e-invoicing compliance with ERP-integrated solutions.",
  },
  "/services-netsuite": {
    title: "Oracle NetSuite Implementation & Support | Cloudare",
    description:
      "Oracle NetSuite implementation, customization and support across India and globally.",
  },
  "/services-odoo": {
    title: "Odoo ERP Implementation & Support | Cloudare",
    description:
      "End-to-end Odoo ERP implementation, customization and ongoing support.",
  },
  "/services-zoho": {
    title: "Zoho ERP, Books and CRM — Implementation and Compliance",
    description:
      "Zoho ERP, Books and CRM implementation with ZATCA and LHDN-compliant invoice workflows. Migration from Tally and QuickBooks supported.",
  },
  "/services-fusion": {
    title: "Oracle Fusion ERP Services | Cloudare",
    description:
      "Oracle Fusion Cloud ERP implementation, integration and managed services.",
  },
  "/services-ebs": {
    title: "Oracle EBS Implementation & Support | Cloudare",
    description:
      "Oracle E-Business Suite (R11i, R12, R12.2) implementation, upgrades and AMS.",
  },
  "/services-oracle": {
    title: "Oracle Fusion, EBS, OIC and VBCS — Implementation Partner",
    description:
      "Oracle Fusion, EBS, OIC, VBCS and APEX. Procure-to-Pay, Record-to-Report and Order-to-Cash. Discovery to go-live to KT.",
  },
  "/services-zatca": {
    title: "KSA ZATCA E-Invoicing — Fatoora Phase 2 Implementation",
    description:
      "ZATCA Phase 2 integration across SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho, Tally and Sage. Fatoora XML, UUID, QR, hash chain. Wave-aligned go-live. ASP-neutral.",
  },
  "/services-gst": {
    title: "India GST E-Invoicing — IRP, E-Way Bill, 2A/2B",
    description:
      "India GST e-invoice IRP, e-way bill, TDS, 2A/2B reconciliation and vendor payment blocking across SAP, Oracle, Dynamics, NetSuite, Zoho, Tally and more.",
  },
  "/services-lhdn": {
    title: "Malaysia LHDN MyInvois — E-Invoicing for Your ERP",
    description:
      "LHDN MyInvois e-invoicing across SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho, Tally and Sage. POS-to-LHDN integrations. Phased rollout aligned to your turnover band.",
  },
  "/services-fta-uae": {
    title: "UAE E-Invoicing — PINT AE Implementation",
    description:
      "UAE FTA-mandated PINT AE e-invoicing across SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho, Tally and Sage. ASP-neutral, five-corner PEPPOL model.",
  },

  // ===== v4 canonical keys =====
  "/tax/ksa": {
    title: "KSA ZATCA E-Invoicing — Fatoora Phase 2 | Cloudare",
    description:
      "ZATCA Phase 2 integration across SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho, Tally and Sage. Fatoora XML, UUID, QR, hash chain. Wave-aligned go-live. ASP-neutral.",
  },
  "/tax/uae": {
    title: "UAE E-Invoicing — PINT AE Implementation | Cloudare",
    description:
      "UAE FTA PINT AE e-invoicing across SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho, Tally and Sage. ASP-neutral, five-corner PEPPOL model.",
  },
  "/tax/india": {
    title: "India GST E-Invoicing — IRP, E-Way Bill, 2A/2B | Cloudare",
    description:
      "India GST e-invoice IRP, e-way bill, TDS, 2A/2B reconciliation and vendor payment blocking across SAP, Oracle, Dynamics, NetSuite, Zoho, Tally and more.",
  },
  "/tax/malaysia": {
    title: "Malaysia LHDN MyInvois E-Invoicing | Cloudare",
    description:
      "LHDN MyInvois e-invoicing across SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho, Tally and Sage. POS-to-LHDN integrations. Phased rollout aligned to your turnover band.",
  },
  "/tax/france": {
    title: "France E-Invoicing — PDP, Factur-X, E-Reporting | Cloudare",
    description:
      "France's facture electronique reform across SAP, Oracle, Dynamics, NetSuite, Odoo, Zoho and Sage: PDP routing, Factur-X, and DGFiP e-reporting. Disponible en francais.",
  },
  "/tax/eu": {
    title: "PEPPOL & EU E-Invoicing — EN 16931, ViDA | Cloudare",
    description:
      "Cross-border e-invoicing over PEPPOL, aligned to EN 16931 and the EU's VAT in the Digital Age (ViDA) direction. Access-point-neutral.",
  },
  "/services/oracle": {
    title: "Oracle Fusion, EBS, OIC and VBCS — Implementation | Cloudare",
    description:
      "Oracle Fusion, EBS, OIC, VBCS and APEX. Procure-to-Pay, Record-to-Report and Order-to-Cash. Discovery to go-live to KT.",
  },
  "/services/zoho": {
    title: "Zoho ERP, Books and CRM — Implementation | Cloudare",
    description:
      "Zoho ERP, Books and CRM implementation with ZATCA and LHDN-compliant invoice workflows. Migration from Tally and QuickBooks supported.",
  },
  "/services/staffing": {
    title: "Global IT Staffing — Oracle, SAP, E-Invoicing | Cloudare",
    description:
      "Oracle, SAP and e-invoicing talent across India, UAE, KSA, US (C2C/W2) and UK. RPO, contract, contract-to-hire and permanent models.",
  },
  "/services/payroll": {
    title: "Payroll and HR Services — Multi-Geography | Cloudare",
    description:
      "Payroll, statutory compliance and HR operations across India, UAE and US. Integration with Oracle HCM, Zoho People and SAP SuccessFactors.",
  },
  "/services/netsuite": {
    title: "Oracle NetSuite Implementation & Support | Cloudare",
    description:
      "Oracle NetSuite implementation, customization and support across India and globally.",
  },
  "/services/odoo": {
    title: "Odoo ERP Implementation & Support | Cloudare",
    description:
      "End-to-end Odoo ERP implementation, customization and ongoing support.",
  },
  "/services/fusion": {
    title: "Oracle Fusion Cloud ERP Services | Cloudare",
    description:
      "Oracle Fusion Cloud ERP implementation, integration and managed services.",
  },
  "/services/ebs": {
    title: "Oracle EBS Implementation & Support | Cloudare",
    description:
      "Oracle E-Business Suite (R11i, R12, R12.2) implementation, upgrades and AMS.",
  },
  "/success-stories": {
    title: "Success Stories | Cloudare Clients",
    description:
      "Real-world stories of how Cloudare helped enterprises succeed with ERP and e-invoicing.",
  },
  "/EventAndWebinars": {
    title: "Events & Webinars | Cloudare",
    description: "Upcoming and past webinars hosted by Cloudare Technologies.",
  },
  "/awardsAndAchievements": {
    title: "Awards & Achievements | Cloudare",
    description:
      "Awards, recognitions and milestones earned by Cloudare Technologies.",
  },
  "/privacy-policy": {
    title: "Privacy Policy — Cloudare Technologies",
    description: "Cloudare Technologies privacy policy: data collection, processing, retention, DPDP Act and GDPR compliance, and your rights.",
  },
  "/terms": {
    title: "Terms and Conditions — Cloudare Technologies",
    description: "Cloudare Technologies terms of service for ERP implementation, e-invoicing compliance, staffing and managed services engagements.",
  },
  "/refund-policy": {
    title: "Refund Policy — Cloudare Technologies",
    description: "Cloudare Technologies refund policy covering professional services, staffing engagements and software licensing arrangements.",
  },
};

// Helper to build canonical URL
export const canonicalFor = (path) => `${SITE.url}${path}`;

// logo.dev publishable token (safe to expose — used in client-side <img> tags).
// Set REACT_APP_LOGODEV_TOKEN in Vercel. Get a free token at https://logo.dev
export const LOGO_DEV_TOKEN = process.env.REACT_APP_LOGODEV_TOKEN || "";

// Client/partner companies shown via logo.dev (domain + display name).
// Add or remove entries here — no image files to manage.
export const CLIENT_COMPANIES = [
  { domain: "oracle.com", name: "Oracle" },
  { domain: "zoho.com", name: "Zoho" },
  { domain: "odoo.com", name: "Odoo" },
  { domain: "caratlane.com", name: "CaratLane" },
  { domain: "bigbasket.com", name: "BigBasket" },
  { domain: "curefoods.in", name: "Curefoods" },
  { domain: "sugunafoods.com", name: "Suguna Foods" },
  { domain: "razorpay.com", name: "Razorpay" },
];
